/**
 * Economics Student Notes - Expanded Study Materials
 * Comprehensive notes for Years 11-13 Economics
 * Based on Vanuatu National Syllabus
 * Last Updated: January 31, 2026
 */

// ============================================
// IMAGE RESOURCES FOR ECONOMICS NOTES
// ============================================
export const economicsImages = {
  market: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800",
  money: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800",
  trade: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800",
  government: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800",
  growth: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
  banking: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800",
  employment: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800",
  inflation: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800"
};

// ============================================
// YEAR 11 ECONOMICS STUDENT NOTES
// ============================================
export const year11EconomicsStudentNotes = {
  strand1: {
    title: "Resource Allocation through the Market System",
    overview: "This strand covers the fundamental concepts of economics including scarcity, choice, and how markets allocate resources through the forces of demand and supply.",
    image: economicsImages.market,
    units: [
      {
        id: "1.1",
        title: "Introduction to Economics",
        image: economicsImages.growth,
        objectives: [
          "Define the economic problem in terms of scarcity, choice, and opportunity cost",
          "Identify and explain the factors of production and their rewards",
          "Draw and interpret the Production Possibility Curve (PPC)",
          "Distinguish between goods and services",
          "Identify the three sectors of the economy"
        ],
        keyTerms: [
          { term: "Economics", definition: "The study of how society allocates scarce resources among unlimited wants. It examines production, distribution, and consumption of goods and services." },
          { term: "Scarcity", definition: "The fundamental economic problem - resources are limited while human wants are unlimited. This forces us to make choices." },
          { term: "Opportunity Cost", definition: "The value of the next best alternative forgone when making a choice. It represents the true cost of any decision." },
          { term: "Factors of Production", definition: "The inputs used to produce goods and services: Land, Labor, Capital, and Enterprise." },
          { term: "Land", definition: "All natural resources including soil, water, minerals, and climate. Reward: Rent." },
          { term: "Labor", definition: "Human effort (physical and mental) used in production. Reward: Wages/Salaries." },
          { term: "Capital", definition: "Man-made resources used to produce other goods (machinery, tools, factories). Reward: Interest." },
          { term: "Enterprise", definition: "The ability to organize other factors of production and take risks. Reward: Profit." },
          { term: "Production Possibility Curve (PPC)", definition: "A graph showing all possible combinations of two goods that can be produced with available resources." },
          { term: "Economic Growth", definition: "An increase in the productive capacity of an economy, shown by an outward shift of the PPC." }
        ],
        studyNotes: `
## Introduction to Economics

### The Economic Problem
The fundamental economic problem arises because:
- **Human wants are unlimited** - people always want more
- **Resources are limited (scarce)** - there is only so much available
- This forces society to make **choices** about what to produce

### The Three Basic Economic Questions
Every society must answer:
1. **WHAT** to produce? (Which goods and services?)
2. **HOW** to produce? (What methods and resources?)
3. **FOR WHOM** to produce? (Who receives the output?)

---

## Factors of Production

| Factor | Definition | Examples | Reward |
|--------|------------|----------|--------|
| Land | Natural resources | Soil, minerals, water, forests | Rent |
| Labor | Human effort | Workers, managers, professionals | Wages |
| Capital | Man-made resources | Machines, tools, buildings | Interest |
| Enterprise | Risk-taking & organizing | Entrepreneurs, business owners | Profit |

### Key Points:
- **Land** includes ALL natural resources (not just soil)
- **Capital** is NOT money - it's physical productive equipment
- **Enterprise** bears the risk of loss and earns profit if successful

---

## Opportunity Cost

**Definition:** The value of the next best alternative given up when making a choice.

### Example:
If you have VT 1000 and choose to buy a book instead of food:
- The opportunity cost of the book = the food you didn't buy
- Opportunity cost is not money - it's what you sacrifice

### Why It Matters:
- Every choice has an opportunity cost
- Resources used for one purpose cannot be used for another
- Good decision-making considers opportunity costs

---

## Production Possibility Curve (PPC)

### What the PPC Shows:
- All possible combinations of two goods with available resources
- The trade-offs an economy faces

### Key Points on the PPC:

| Point Position | Meaning |
|---------------|---------|
| On the curve | Efficient production (full resource use) |
| Inside the curve | Inefficiency or unemployment |
| Outside the curve | Currently unattainable |

### Shifts of the PPC:
**Outward Shift (Economic Growth):**
- More resources discovered
- Technology improves
- Labor force grows
- Better education/training

**Inward Shift (Economic Decline):**
- Natural disaster
- War destroys resources
- Population decline
        `,
        examTips: [
          "Always define opportunity cost as the NEXT BEST alternative, not just any alternative",
          "Remember: Capital is NOT money - it's machinery, tools, buildings",
          "When drawing PPC, label both axes with the two goods being compared",
          "Points inside the PPC = unemployment or inefficiency, not impossibility"
        ],
        practiceQuestions: [
          { question: "Define the economic problem.", answer: "The economic problem is that resources are limited/scarce but human wants are unlimited, forcing society to make choices about resource allocation." },
          { question: "What is the opportunity cost of studying economics tonight?", answer: "The next best alternative forgone - e.g., leisure time, sleep, or studying another subject." },
          { question: "Name the four factors of production and their rewards.", answer: "Land (Rent), Labor (Wages), Capital (Interest), Enterprise (Profit)" },
          { question: "What does a point inside the PPC represent?", answer: "Underutilization of resources - either unemployment or inefficient production" }
        ]
      },
      {
        id: "1.2",
        title: "Demand Analysis",
        image: economicsImages.market,
        objectives: [
          "Define demand and explain the law of demand",
          "Draw and interpret the demand curve",
          "Distinguish between movements along and shifts of the demand curve",
          "Identify factors affecting demand",
          "Calculate and interpret price elasticity of demand"
        ],
        keyTerms: [
          { term: "Demand", definition: "The quantity of a good or service that consumers are willing and able to buy at various prices in a given time period." },
          { term: "Law of Demand", definition: "Ceteris paribus (all else equal), as price rises, quantity demanded falls; as price falls, quantity demanded rises." },
          { term: "Demand Curve", definition: "A graph showing the relationship between price and quantity demanded, typically downward sloping." },
          { term: "Movement Along", definition: "A change in quantity demanded caused by a change in the good's own price." },
          { term: "Shift of Demand", definition: "A change in demand caused by non-price factors, moving the entire curve left or right." },
          { term: "Substitute Good", definition: "A good that can be used instead of another (e.g., butter and margarine). Price rise in one increases demand for the other." },
          { term: "Complementary Good", definition: "A good used together with another (e.g., cars and petrol). Price rise in one decreases demand for the other." },
          { term: "Price Elasticity of Demand (PED)", definition: "A measure of how responsive quantity demanded is to a change in price. PED = %ΔQd / %ΔP" },
          { term: "Elastic Demand", definition: "PED > 1: Quantity demanded is highly responsive to price changes (luxury goods)." },
          { term: "Inelastic Demand", definition: "PED < 1: Quantity demanded is not very responsive to price changes (necessities)." }
        ],
        studyNotes: `
## Demand

### Definition
**Demand** = Willingness + Ability to buy at various prices

Important: Demand is NOT just wanting something - you must be able to pay!

---

## The Law of Demand

**"Ceteris paribus, as price increases, quantity demanded decreases, and vice versa."**

### Why Does This Happen?
1. **Income Effect**: Higher prices mean your income buys less
2. **Substitution Effect**: You switch to cheaper alternatives
3. **Diminishing Marginal Utility**: Extra units give less satisfaction

---

## The Demand Curve

- **Downward sloping** (negative gradient)
- Price on Y-axis, Quantity on X-axis
- Shows inverse relationship between P and Qd

### Movement vs Shift

| Movement Along | Shift of Curve |
|----------------|----------------|
| Caused by price change | Caused by non-price factors |
| Move along same curve | Entire curve moves |
| A → B on curve | Curve moves left or right |

---

## Factors That SHIFT Demand

**Increase Demand (Shift Right):**
- Income rises (for normal goods)
- Price of substitute rises
- Price of complement falls
- Population increases
- Favorable tastes/preferences
- Expected future price rises

**Decrease Demand (Shift Left):**
- Income falls
- Price of substitute falls
- Price of complement rises
- Population decreases
- Unfavorable tastes/preferences

---

## Price Elasticity of Demand (PED)

**Formula:**
PED = (% Change in Quantity Demanded) / (% Change in Price)

### Interpreting PED:

| PED Value | Description | Example |
|-----------|-------------|---------|
| PED > 1 | Elastic | Luxury goods, items with substitutes |
| PED = 1 | Unit elastic | - |
| PED < 1 | Inelastic | Necessities, addictive goods |
| PED = 0 | Perfectly inelastic | Life-saving medicine |
| PED = ∞ | Perfectly elastic | Perfect substitutes |

### Example Calculation:
Price rises from VT 100 to VT 120 (20% increase)
Quantity demanded falls from 50 to 40 units (20% decrease)

PED = 20% / 20% = 1 (Unit Elastic)
        `,
        examTips: [
          "Always use 'ceteris paribus' when stating the law of demand",
          "Demand includes both willingness AND ability to pay",
          "Movement = price change; Shift = everything else",
          "PED is usually negative but we often ignore the sign"
        ],
        practiceQuestions: [
          { question: "State the law of demand.", answer: "Ceteris paribus, as the price of a good rises, the quantity demanded falls, and vice versa." },
          { question: "If the price of coffee rises, what happens to demand for tea?", answer: "Demand for tea increases (shifts right) because tea and coffee are substitutes." },
          { question: "Calculate PED if a 10% price rise causes a 30% fall in quantity demanded.", answer: "PED = 30%/10% = 3 (Elastic demand)" }
        ]
      },
      {
        id: "1.3",
        title: "Supply Analysis",
        image: economicsImages.trade,
        objectives: [
          "Define supply and explain the law of supply",
          "Draw and interpret the supply curve",
          "Distinguish between movements along and shifts of the supply curve",
          "Identify factors affecting supply",
          "Explain economies and diseconomies of scale"
        ],
        keyTerms: [
          { term: "Supply", definition: "The quantity of a good or service that producers are willing and able to offer for sale at various prices in a given time period." },
          { term: "Law of Supply", definition: "Ceteris paribus, as price rises, quantity supplied rises; as price falls, quantity supplied falls." },
          { term: "Supply Curve", definition: "A graph showing the relationship between price and quantity supplied, typically upward sloping." },
          { term: "Economies of Scale", definition: "Cost advantages that firms gain when production increases - average costs fall as output rises." },
          { term: "Diseconomies of Scale", definition: "Cost disadvantages when a firm becomes too large - average costs rise as output increases." },
          { term: "Fixed Costs", definition: "Costs that don't change with output level (e.g., rent, insurance)." },
          { term: "Variable Costs", definition: "Costs that change with output level (e.g., raw materials, wages)." }
        ],
        studyNotes: `
## Supply

### Definition
**Supply** = Willingness + Ability to produce and sell at various prices

---

## The Law of Supply

**"Ceteris paribus, as price increases, quantity supplied increases, and vice versa."**

### Why?
- Higher prices = higher profit potential
- Producers are motivated to produce more
- New producers enter the market

---

## The Supply Curve

- **Upward sloping** (positive gradient)
- Price on Y-axis, Quantity on X-axis
- Shows positive relationship between P and Qs

---

## Factors That SHIFT Supply

**Increase Supply (Shift Right):**
- Cost of production falls
- Technology improves
- Government subsidies
- Good weather (for agriculture)
- More producers enter market

**Decrease Supply (Shift Left):**
- Cost of production rises
- Taxes increase
- Bad weather
- Natural disasters
- Producers leave market

---

## Economies of Scale

### Internal Economies (within the firm):
1. **Technical**: Large machines are more efficient
2. **Purchasing**: Bulk buying discounts
3. **Financial**: Better loan rates for big firms
4. **Managerial**: Specialist managers
5. **Marketing**: Advertising costs spread over more units

### External Economies (industry-wide):
- Shared infrastructure
- Skilled labor pool
- Support industries develop

### Diseconomies of Scale
- Communication problems
- Coordination difficulties
- Worker alienation
- Bureaucracy increases
        `,
        examTips: [
          "Supply curve slopes UPWARD (opposite to demand)",
          "Technology improving ALWAYS shifts supply right",
          "Higher costs of production shift supply LEFT",
          "Economies of scale explain why big firms can charge lower prices"
        ],
        practiceQuestions: [
          { question: "State the law of supply.", answer: "Ceteris paribus, as the price of a good rises, the quantity supplied rises, and vice versa." },
          { question: "Give three factors that could shift the supply curve to the left.", answer: "Higher wages, increased taxes, natural disaster, rising raw material costs" },
          { question: "What are economies of scale?", answer: "Cost advantages that result from increased scale of production - average costs fall as output rises." }
        ]
      },
      {
        id: "1.4",
        title: "Market Equilibrium",
        objectives: [
          "Determine market equilibrium price and quantity",
          "Analyze excess supply and demand situations",
          "Explain effects of price controls",
          "Understand different market structures"
        ],
        keyTerms: [
          { term: "Market Equilibrium", definition: "The point where quantity demanded equals quantity supplied. No tendency for price to change." },
          { term: "Equilibrium Price", definition: "The price at which the market clears - quantity demanded equals quantity supplied." },
          { term: "Excess Demand", definition: "When quantity demanded exceeds quantity supplied at a given price - creates upward pressure on price." },
          { term: "Excess Supply", definition: "When quantity supplied exceeds quantity demanded at a given price - creates downward pressure on price." },
          { term: "Price Ceiling", definition: "A maximum price set by government below equilibrium - causes shortages." },
          { term: "Price Floor", definition: "A minimum price set by government above equilibrium - causes surpluses." }
        ],
        studyNotes: `
## Market Equilibrium

### Finding Equilibrium
Equilibrium occurs where the demand and supply curves intersect:
- **Qd = Qs** (Quantity demanded equals quantity supplied)
- No shortage or surplus
- No tendency for price to change

---

## Market Disequilibrium

### Excess Demand (Shortage)
- Price is BELOW equilibrium
- Qd > Qs
- Consumers compete, driving price UP
- Market moves toward equilibrium

### Excess Supply (Surplus)
- Price is ABOVE equilibrium
- Qs > Qd
- Producers cut prices to sell stock
- Market moves toward equilibrium

---

## Price Controls

### Price Ceiling (Maximum Price)
- Set BELOW equilibrium to protect consumers
- Examples: Rent controls, food price caps
- Consequence: SHORTAGE (Qd > Qs)
- Problems: Black markets, queues, rationing

### Price Floor (Minimum Price)
- Set ABOVE equilibrium to protect producers
- Examples: Minimum wage, agricultural price supports
- Consequence: SURPLUS (Qs > Qd)
- Problems: Unemployment (for wages), unsold goods

---

## Effects of Shifts in Demand or Supply

| Shift | Effect on Price | Effect on Quantity |
|-------|-----------------|-------------------|
| Demand increases | Price rises | Quantity rises |
| Demand decreases | Price falls | Quantity falls |
| Supply increases | Price falls | Quantity rises |
| Supply decreases | Price rises | Quantity falls |
        `,
        examTips: [
          "At equilibrium: Qd = Qs (no surplus or shortage)",
          "Price ceiling BELOW equilibrium = shortage",
          "Price floor ABOVE equilibrium = surplus",
          "Always draw diagrams to show market effects"
        ],
        practiceQuestions: [
          { question: "What is market equilibrium?", answer: "The point where quantity demanded equals quantity supplied, with no tendency for price to change." },
          { question: "If price is below equilibrium, what happens?", answer: "Excess demand (shortage) occurs, putting upward pressure on price until equilibrium is restored." },
          { question: "What is the effect of a price ceiling set below equilibrium?", answer: "It creates a shortage (excess demand) as quantity demanded exceeds quantity supplied at the controlled price." }
        ]
      }
    ]
  },
  strand2: {
    title: "Resource Allocation through the Public Sector",
    overview: "This strand examines the role of government in the economy, including taxation, public goods, and policies to address market failures and inequality.",
    image: economicsImages.government,
    units: [
      {
        id: "2.1",
        title: "Government Intervention",
        image: economicsImages.government,
        objectives: [
          "Explain reasons for government intervention in the economy",
          "Describe the economic functions of government",
          "Identify different types of taxes",
          "Analyze effects of taxation"
        ],
        keyTerms: [
          { term: "Market Failure", definition: "When free markets fail to allocate resources efficiently, leading to over or under-production of certain goods." },
          { term: "Public Good", definition: "A good that is non-rivalrous and non-excludable. Cannot be provided by private markets (e.g., national defense)." },
          { term: "Merit Good", definition: "A good with positive externalities that is under-consumed by the market (e.g., education, healthcare)." },
          { term: "Demerit Good", definition: "A good with negative externalities that is over-consumed by the market (e.g., cigarettes, alcohol)." },
          { term: "Direct Tax", definition: "A tax on income or wealth paid directly to the government (e.g., income tax, company tax)." },
          { term: "Indirect Tax", definition: "A tax on spending, added to the price of goods (e.g., GST, VAT, excise duties)." },
          { term: "Progressive Tax", definition: "Tax rate increases as income increases - takes a larger percentage from high earners." },
          { term: "Regressive Tax", definition: "Tax rate decreases as income increases - takes a larger percentage from low earners." },
          { term: "Proportional Tax", definition: "Tax rate stays the same regardless of income - flat percentage for all." }
        ],
        studyNotes: `
## Why Does Government Intervene?

### Reasons for Intervention:
1. **Correct Market Failures** - Markets don't always work efficiently
2. **Provide Public Goods** - Private markets won't provide them
3. **Reduce Inequality** - Redistribute income and wealth
4. **Stabilize the Economy** - Control inflation, unemployment
5. **Protect Consumers** - Ensure safety and fair practices

---

## Types of Goods

### Public Goods
Characteristics:
- **Non-rivalrous**: One person's use doesn't reduce availability
- **Non-excludable**: Cannot prevent anyone from using it

Examples: Street lights, national defense, public parks

### Merit Goods
- Provide benefits to society beyond the individual
- Under-consumed in free markets
- Government encourages consumption (subsidies, free provision)

Examples: Education, healthcare, vaccinations

### Demerit Goods
- Cause harm to society beyond the individual
- Over-consumed in free markets
- Government discourages consumption (taxes, bans)

Examples: Cigarettes, alcohol, gambling

---

## Taxation

### Direct vs Indirect Taxes

| Direct Taxes | Indirect Taxes |
|--------------|----------------|
| On income/wealth | On spending |
| Paid directly to govt | Added to prices |
| Income tax, company tax | GST, VAT, customs |
| Cannot be passed on | Can be passed to consumers |

### Tax Systems

| System | How It Works | Example |
|--------|--------------|---------|
| Progressive | Rate rises with income | Income tax brackets |
| Regressive | Rate falls with income | GST (same rate, but poor spend more of income) |
| Proportional | Same rate for all | Flat tax |
        `,
        examTips: [
          "Public goods have TWO characteristics: non-rivalrous AND non-excludable",
          "Merit goods = under-consumed; Demerit goods = over-consumed",
          "Progressive tax = fair? Helps reduce inequality",
          "GST is regressive because poor spend higher % of income"
        ],
        practiceQuestions: [
          { question: "What are the two characteristics of a public good?", answer: "Non-rivalrous (one person's use doesn't reduce availability) and non-excludable (cannot prevent anyone from using it)." },
          { question: "Give two examples of merit goods.", answer: "Education and healthcare (also: vaccinations, libraries, museums)" },
          { question: "Explain why GST is considered regressive.", answer: "GST charges the same rate to all, but poor people spend a higher proportion of their income on goods, so they pay a higher share of their income in GST." }
        ]
      },
      {
        id: "2.2",
        title: "Equality and Equity",
        objectives: [
          "Define equality and equity",
          "Identify causes of economic inequalities",
          "Analyze policies to reduce inequality"
        ],
        keyTerms: [
          { term: "Equality", definition: "Treating everyone the same or giving equal distribution of resources regardless of circumstances." },
          { term: "Equity", definition: "Fairness - distributing resources according to need so outcomes are fair." },
          { term: "Income Inequality", definition: "Unequal distribution of income among individuals or households in an economy." },
          { term: "Wealth Inequality", definition: "Unequal distribution of assets (property, savings, investments) among individuals." },
          { term: "Poverty", definition: "A state where individuals lack sufficient income to meet basic needs (food, shelter, clothing)." },
          { term: "Transfer Payments", definition: "Government payments to individuals not in exchange for goods or services (e.g., pensions, welfare)." }
        ],
        studyNotes: `
## Equality vs Equity

### Equality
- Treating everyone exactly the same
- Same resources, opportunities for all
- May not account for different needs

### Equity
- Fairness based on individual needs
- Different treatment to achieve fair outcomes
- Recognizes people start from different positions

**Example:**
- Equality: Give every student the same textbook
- Equity: Give students textbooks suited to their reading level

---

## Causes of Inequality

1. **Education Differences** - More education = higher income
2. **Skills and Training** - Specialized skills earn more
3. **Inheritance** - Wealth passed between generations
4. **Discrimination** - Gender, race, age barriers
5. **Regional Differences** - Urban vs rural income gaps
6. **Market Forces** - Some jobs pay more than others

---

## Government Policies to Reduce Inequality

### Through Taxation:
- Progressive income tax
- Inheritance/estate taxes
- Wealth taxes

### Through Spending:
- Free education
- Public healthcare
- Transfer payments (welfare, pensions)
- Subsidized housing

### Through Regulation:
- Minimum wage laws
- Anti-discrimination laws
- Equal pay legislation
        `,
        examTips: [
          "Equality = same for all; Equity = fair based on needs",
          "Remember multiple causes of inequality for exam answers",
          "Link policies to their effects on different groups"
        ],
        practiceQuestions: [
          { question: "Distinguish between equality and equity.", answer: "Equality means treating everyone the same; equity means treating people fairly according to their needs to achieve fair outcomes." },
          { question: "Give three causes of income inequality.", answer: "Differences in education, skills, inheritance, discrimination, regional location" }
        ]
      }
    ]
  },
  strand3: {
    title: "Money",
    overview: "This strand covers the nature and functions of money, the banking system, money supply, and inflation.",
    image: economicsImages.money,
    units: [
      {
        id: "3.1",
        title: "Introduction to Money",
        image: economicsImages.banking,
        objectives: [
          "Describe the evolution of money",
          "Explain the four functions of money",
          "Identify characteristics of good money",
          "Describe the role of financial institutions"
        ],
        keyTerms: [
          { term: "Barter", definition: "Direct exchange of goods for goods without using money. Requires 'double coincidence of wants'." },
          { term: "Money", definition: "Anything generally accepted as a medium of exchange in transactions." },
          { term: "Medium of Exchange", definition: "The primary function of money - facilitates buying and selling without barter." },
          { term: "Store of Value", definition: "Money can be saved and used later, holding its value over time." },
          { term: "Unit of Account", definition: "Money provides a common measure to express the value of goods and services." },
          { term: "Standard of Deferred Payment", definition: "Money allows debts to be expressed and paid in the future." },
          { term: "Fiat Money", definition: "Money declared legal tender by government, not backed by a commodity." },
          { term: "Legal Tender", definition: "Money that must be accepted for payment of debts by law." }
        ],
        studyNotes: `
## The Evolution of Money

### From Barter to Modern Money:
1. **Barter** - Direct exchange of goods
2. **Commodity Money** - Using valuable items (gold, cattle, shells)
3. **Metallic Money** - Coins made of precious metals
4. **Paper Money** - Notes backed by gold (Gold Standard)
5. **Fiat Money** - Government-declared currency
6. **Electronic Money** - Digital payments, cryptocurrencies

### Problems with Barter:
- **Double coincidence of wants** - Both parties must want what the other has
- **Indivisibility** - How to make change with a cow?
- **Storage problems** - Goods may spoil
- **No standard of value** - Difficult to compare values

---

## Functions of Money

### 1. Medium of Exchange
- The PRIMARY function
- Eliminates need for barter
- Facilitates trade and specialization

### 2. Store of Value
- Save purchasing power for future use
- Must maintain value over time
- Inflation erodes this function

### 3. Unit of Account
- Standard measure of value
- Allows comparison of prices
- Enables accounting and record-keeping

### 4. Standard of Deferred Payment
- Allows credit and debt contracts
- Future payments can be specified
- Basis of lending and borrowing

---

## Characteristics of Good Money

1. **Acceptability** - Widely accepted in exchange
2. **Durability** - Lasts a long time
3. **Portability** - Easy to carry
4. **Divisibility** - Can be divided into smaller units
5. **Homogeneity** - All units are identical
6. **Scarcity** - Limited supply to maintain value
7. **Stability** - Value remains relatively constant
        `,
        examTips: [
          "Know all FOUR functions of money for the exam",
          "Double coincidence of wants = main problem with barter",
          "Inflation reduces money's ability to store value"
        ],
        practiceQuestions: [
          { question: "List the four functions of money.", answer: "Medium of exchange, store of value, unit of account, standard of deferred payment" },
          { question: "Why did barter systems fail?", answer: "Required double coincidence of wants, goods were indivisible, storage problems, no standard measure of value" }
        ]
      },
      {
        id: "3.2",
        title: "Inflation",
        image: economicsImages.inflation,
        objectives: [
          "Define inflation and deflation",
          "Describe methods of measuring inflation",
          "Identify causes of inflation",
          "Analyze effects of inflation"
        ],
        keyTerms: [
          { term: "Inflation", definition: "A sustained increase in the general price level of goods and services over time." },
          { term: "Deflation", definition: "A sustained decrease in the general price level - prices falling over time." },
          { term: "Consumer Price Index (CPI)", definition: "A measure of inflation based on the price changes of a 'basket' of typical consumer goods." },
          { term: "Demand-Pull Inflation", definition: "Inflation caused by excessive aggregate demand - 'too much money chasing too few goods'." },
          { term: "Cost-Push Inflation", definition: "Inflation caused by rising production costs being passed on to consumers." },
          { term: "Hyperinflation", definition: "Extremely rapid and out-of-control inflation, often over 50% per month." },
          { term: "Real Value", definition: "The value of money adjusted for inflation - what it can actually buy." },
          { term: "Nominal Value", definition: "The face value of money without adjusting for inflation." }
        ],
        studyNotes: `
## What is Inflation?

**Inflation** = A sustained increase in the general price level

Key points:
- It's about the GENERAL price level, not individual prices
- Must be SUSTAINED (not a one-time jump)
- Measured as a percentage increase per year

---

## Measuring Inflation

### Consumer Price Index (CPI)
1. Select a 'basket' of typical goods and services
2. Calculate the cost of the basket each period
3. Compare to a base year

**Inflation Rate = [(CPI Year 2 - CPI Year 1) / CPI Year 1] × 100%**

### Example:
- Base year CPI = 100
- Current year CPI = 105
- Inflation rate = 5%

---

## Causes of Inflation

### 1. Demand-Pull Inflation
"Too much money chasing too few goods"

Causes:
- Increased consumer spending
- Government spending increases
- Tax cuts
- Exports boom
- Money supply increases

### 2. Cost-Push Inflation
Rising costs push up prices

Causes:
- Wage increases
- Raw material costs rise
- Import prices increase (weaker currency)
- Higher taxes on businesses
- Natural disasters

---

## Effects of Inflation

### Negative Effects:
- **Savers lose** - Money loses value
- **Fixed income earners suffer** - Purchasing power falls
- **Menu costs** - Constant price changes
- **Uncertainty** - Hard to plan for future
- **Reduced international competitiveness**

### Who Benefits?
- **Borrowers** - Repay with 'cheaper' money
- **Asset holders** - Property values rise
- **Government** - Tax revenues rise, debt burden falls
        `,
        examTips: [
          "Inflation must be SUSTAINED and GENERAL, not one-off or single items",
          "Know both demand-pull AND cost-push causes",
          "Inflation hurts savers and fixed income earners",
          "Deflation is NOT always good - can cause economic problems"
        ],
        practiceQuestions: [
          { question: "Define inflation.", answer: "A sustained increase in the general price level of goods and services over time." },
          { question: "Explain demand-pull inflation.", answer: "Inflation caused by excessive aggregate demand - too much money chasing too few goods. The demand curve shifts right faster than supply can respond." },
          { question: "Who loses from high inflation?", answer: "Savers (money loses value), fixed income earners (purchasing power falls), creditors (repaid in 'cheaper' money)" }
        ]
      }
    ]
  },
  strand4: {
    title: "Trade and Payments",
    overview: "This strand examines international trade, balance of payments, exchange rates, and the circular flow of income in the economy.",
    image: economicsImages.trade,
    units: [
      {
        id: "4.1",
        title: "International Trade",
        image: economicsImages.trade,
        objectives: [
          "Explain the reasons for international trade",
          "Understand comparative advantage",
          "Analyze trade in the Vanuatu context"
        ],
        keyTerms: [
          { term: "International Trade", definition: "The exchange of goods and services between countries." },
          { term: "Exports", definition: "Goods and services sold to other countries." },
          { term: "Imports", definition: "Goods and services bought from other countries." },
          { term: "Absolute Advantage", definition: "When a country can produce a good using fewer resources than another country." },
          { term: "Comparative Advantage", definition: "When a country can produce a good at a lower opportunity cost than another country." },
          { term: "Trade Agreement", definition: "An arrangement between countries to reduce or eliminate trade barriers." }
        ],
        studyNotes: `
## Why Countries Trade

1. **Resource differences** - Countries have different natural resources
2. **Climate differences** - Some goods can only grow in certain climates
3. **Specialization** - Focus on what you're best at
4. **Economies of scale** - Larger markets allow lower costs
5. **Greater variety** - Access to goods not produced locally

---

## Comparative Advantage

A country should specialize in goods where it has the **lowest opportunity cost**.

Even if one country is better at producing everything, BOTH countries benefit from specialization and trade.

---

## Vanuatu's Trade

**Main Exports:**
- Copra and coconut oil
- Beef
- Kava
- Cocoa
- Timber
- Tourism services

**Main Imports:**
- Machinery and equipment
- Food products
- Fuel
- Manufactured goods
        `,
        examTips: [
          "Comparative advantage is about OPPORTUNITY COST, not absolute ability",
          "Both countries benefit from trade based on comparative advantage",
          "Know Vanuatu's main exports and imports"
        ],
        practiceQuestions: [
          { question: "What is comparative advantage?", answer: "When a country can produce a good at a lower opportunity cost than another country." },
          { question: "Name two of Vanuatu's main exports.", answer: "Copra, beef, kava, cocoa, timber, or tourism services" }
        ]
      },
      {
        id: "4.2",
        title: "Balance of Payments",
        objectives: [
          "Define balance of payments",
          "Distinguish between current and capital accounts",
          "Analyze trade deficits and surpluses"
        ],
        keyTerms: [
          { term: "Balance of Payments", definition: "A record of all economic transactions between a country and the rest of the world." },
          { term: "Current Account", definition: "Records trade in goods and services, income, and transfers." },
          { term: "Capital Account", definition: "Records investment flows and financial transactions between countries." },
          { term: "Trade Balance", definition: "The difference between exports and imports of goods and services." },
          { term: "Trade Deficit", definition: "When imports exceed exports (negative trade balance)." },
          { term: "Trade Surplus", definition: "When exports exceed imports (positive trade balance)." }
        ],
        studyNotes: `
## Balance of Payments

### Current Account
Records:
- Trade in goods (visible trade)
- Trade in services (invisible trade)
- Investment income
- Transfers (remittances, aid)

### Capital Account
Records:
- Foreign direct investment
- Portfolio investment
- Loans and borrowing
- Reserve assets

---

## Trade Balance

**Trade Balance = Exports - Imports**

| Situation | Meaning |
|-----------|---------|
| Surplus | Exports > Imports |
| Deficit | Imports > Exports |
| Balanced | Exports = Imports |
        `,
        examTips: [
          "Current account = trade + income + transfers",
          "Capital account = investment + financial flows",
          "Trade deficit is not always bad - may indicate strong consumer demand"
        ],
        practiceQuestions: [
          { question: "What is a trade deficit?", answer: "When a country's imports exceed its exports." },
          { question: "What is recorded in the current account?", answer: "Trade in goods and services, investment income, and transfers." }
        ]
      },
      {
        id: "4.3",
        title: "Exchange Rates",
        objectives: [
          "Define exchange rate",
          "Distinguish between fixed and floating exchange rates",
          "Analyze factors affecting exchange rates"
        ],
        keyTerms: [
          { term: "Exchange Rate", definition: "The price of one currency in terms of another currency." },
          { term: "Fixed Exchange Rate", definition: "An exchange rate set and maintained by the government/central bank." },
          { term: "Floating Exchange Rate", definition: "An exchange rate determined by market forces of supply and demand." },
          { term: "Appreciation", definition: "An increase in the value of a currency relative to another." },
          { term: "Depreciation", definition: "A decrease in the value of a currency relative to another." }
        ],
        studyNotes: `
## Exchange Rates

### Fixed Exchange Rate
- Set by government/central bank
- More stable and predictable
- Requires foreign currency reserves
- May not reflect true market value

### Floating Exchange Rate
- Determined by supply and demand
- Adjusts automatically
- Can be volatile
- Reflects market conditions

---

## Factors Affecting Exchange Rates

1. **Interest rates** - Higher rates attract foreign investment
2. **Inflation** - Higher inflation weakens currency
3. **Trade balance** - Surplus strengthens currency
4. **Economic growth** - Strong growth attracts investment
5. **Political stability** - Uncertainty weakens currency

---

## Effects of Exchange Rate Changes

| Change | Effect on Exports | Effect on Imports |
|--------|-------------------|-------------------|
| Appreciation | More expensive | Cheaper |
| Depreciation | Cheaper | More expensive |
        `,
        examTips: [
          "Appreciation = currency gets stronger (buys more foreign currency)",
          "Depreciation = currency gets weaker (buys less foreign currency)",
          "High interest rates attract foreign investment, strengthening currency"
        ],
        practiceQuestions: [
          { question: "What happens to exports when a currency appreciates?", answer: "Exports become more expensive for foreign buyers, so exports may decrease." },
          { question: "What is a floating exchange rate?", answer: "An exchange rate determined by market forces of supply and demand for the currency." }
        ]
      },
      {
        id: "4.4",
        title: "Circular Flow of Income",
        objectives: [
          "Explain the circular flow model",
          "Identify injections and withdrawals",
          "Understand GDP and economic growth"
        ],
        keyTerms: [
          { term: "Circular Flow", definition: "A model showing how money flows between households and firms in an economy." },
          { term: "Injections", definition: "Additions to the circular flow: Investment (I), Government spending (G), Exports (X)." },
          { term: "Withdrawals", definition: "Removals from the circular flow: Savings (S), Taxes (T), Imports (M)." },
          { term: "Gross Domestic Product (GDP)", definition: "The total value of all final goods and services produced in a country in a year." },
          { term: "Economic Growth", definition: "An increase in real GDP over time - the economy produces more goods and services." }
        ],
        studyNotes: `
## The Circular Flow Model

### Basic Flow:
Households → (Labor) → Firms
Firms → (Wages) → Households
Households → (Spending) → Firms
Firms → (Goods/Services) → Households

### Injections (J)
Add money to the flow:
- **I** = Investment
- **G** = Government spending
- **X** = Exports

### Withdrawals (W)
Remove money from the flow:
- **S** = Savings
- **T** = Taxes
- **M** = Imports

---

## Equilibrium

The economy is in equilibrium when:
**Injections = Withdrawals**
**I + G + X = S + T + M**

If J > W → Economy expands
If J < W → Economy contracts

---

## GDP

**GDP = C + I + G + (X - M)**

Where:
- C = Consumer spending
- I = Investment
- G = Government spending
- X = Exports
- M = Imports
        `,
        examTips: [
          "Injections: I + G + X (add to economy)",
          "Withdrawals: S + T + M (remove from economy)",
          "Equilibrium: Injections = Withdrawals"
        ],
        practiceQuestions: [
          { question: "What are the three injections in the circular flow?", answer: "Investment (I), Government spending (G), and Exports (X)" },
          { question: "What happens if injections exceed withdrawals?", answer: "The economy expands - total spending and output increase." }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 12 ECONOMICS STUDENT NOTES
// ============================================
export const year12EconomicsStudentNotes = {
  strand1: {
    title: "Advanced Market Analysis",
    overview: "Building on Year 11 concepts with deeper analysis of market structures, elasticity, and market failures.",
    units: [
      {
        id: "1.1",
        title: "Market Structures",
        objectives: [
          "Compare different market structures",
          "Analyze perfect and imperfect competition",
          "Evaluate monopoly and oligopoly markets"
        ],
        keyTerms: [
          { term: "Perfect Competition", definition: "A market with many buyers and sellers, identical products, free entry/exit, and perfect information." },
          { term: "Monopoly", definition: "A market with a single seller who controls the entire supply of a unique product." },
          { term: "Oligopoly", definition: "A market dominated by a few large firms who are interdependent in their decision-making." },
          { term: "Monopolistic Competition", definition: "Many sellers of differentiated products with some market power." },
          { term: "Barriers to Entry", definition: "Factors that prevent new firms from entering a market (e.g., high startup costs, patents, regulations)." }
        ],
        studyNotes: `
## Market Structures Comparison

| Feature | Perfect Competition | Monopoly | Oligopoly |
|---------|---------------------|----------|-----------|
| Number of firms | Many | One | Few |
| Product | Identical | Unique | Similar/Different |
| Barriers to entry | None | High | Significant |
| Price control | None (price taker) | Significant | Some |
| Examples | Agriculture | Utilities | Airlines, Banks |

---

## Perfect Competition
- Many small firms
- Identical (homogeneous) products
- Free entry and exit
- Perfect information
- Firms are "price takers"

## Monopoly
- Single seller
- Unique product, no substitutes
- High barriers to entry
- Price maker
- Can earn supernormal profits

## Oligopoly
- Few dominant firms
- Interdependent decisions
- May collude or compete
- Significant barriers to entry
        `,
        practiceQuestions: [
          { question: "What are the characteristics of perfect competition?", answer: "Many small firms, identical products, free entry/exit, perfect information, price takers" },
          { question: "Why can monopolies charge higher prices?", answer: "No competition, unique product with no substitutes, high barriers prevent new entrants" }
        ]
      },
      {
        id: "1.2",
        title: "Market Failure and Government Response",
        objectives: [
          "Explain causes of market failure",
          "Analyze externalities",
          "Evaluate government responses to market failure"
        ],
        keyTerms: [
          { term: "Externality", definition: "A cost or benefit that affects parties not directly involved in a transaction." },
          { term: "Positive Externality", definition: "A benefit to third parties (e.g., education benefits society)." },
          { term: "Negative Externality", definition: "A cost imposed on third parties (e.g., pollution from factories)." },
          { term: "Social Cost", definition: "Private cost plus external costs - the true cost to society." },
          { term: "Social Benefit", definition: "Private benefit plus external benefits - the true benefit to society." }
        ],
        studyNotes: `
## Externalities

### Negative Externalities
- Costs imposed on third parties
- Examples: Pollution, noise, congestion
- Market produces TOO MUCH

**Solutions:**
- Taxes (Pigouvian tax)
- Regulations
- Tradeable permits

### Positive Externalities
- Benefits to third parties
- Examples: Education, vaccination, research
- Market produces TOO LITTLE

**Solutions:**
- Subsidies
- Government provision
- Legislation (e.g., compulsory education)

---

## Social Cost and Benefit

**Social Cost = Private Cost + External Costs**
**Social Benefit = Private Benefit + External Benefits**

For efficiency: Social Marginal Benefit = Social Marginal Cost
        `,
        practiceQuestions: [
          { question: "What is a negative externality? Give an example.", answer: "A cost imposed on third parties not involved in a transaction. Example: Factory pollution affecting local residents." },
          { question: "How can government correct negative externalities?", answer: "Through taxes (make polluters pay), regulations (set limits), or tradeable permits (cap and trade)" }
        ]
      }
    ]
  },
  strand2: {
    title: "Macroeconomics",
    overview: "Understanding the economy as a whole - national income, economic growth, and government policy.",
    units: [
      {
        id: "2.1",
        title: "National Income",
        objectives: [
          "Understand GDP and its measurement",
          "Distinguish between real and nominal GDP",
          "Analyze components of aggregate demand"
        ],
        keyTerms: [
          { term: "Gross Domestic Product (GDP)", definition: "The total value of all final goods and services produced within a country in a year." },
          { term: "Real GDP", definition: "GDP adjusted for inflation - measures actual output growth." },
          { term: "Nominal GDP", definition: "GDP at current prices - not adjusted for inflation." },
          { term: "Aggregate Demand", definition: "Total spending in an economy: AD = C + I + G + (X - M)" },
          { term: "Economic Growth", definition: "An increase in real GDP over time - expansion of productive capacity." }
        ],
        studyNotes: `
## Measuring National Income

### GDP = C + I + G + (X - M)

Where:
- **C** = Consumer spending
- **I** = Investment (by businesses)
- **G** = Government spending
- **X** = Exports
- **M** = Imports

---

## Real vs Nominal GDP

| Nominal GDP | Real GDP |
|-------------|----------|
| Current prices | Constant prices |
| Includes inflation | Adjusted for inflation |
| Can be misleading | Better measure of growth |

**Real GDP = Nominal GDP / Price Index × 100**

---

## Economic Growth

**Causes of Growth:**
- More resources (land, labor, capital)
- Better technology
- Improved education
- Investment in infrastructure
- Stable government
        `,
        practiceQuestions: [
          { question: "What is GDP?", answer: "The total value of all final goods and services produced within a country in a given year." },
          { question: "Why is real GDP a better measure than nominal GDP?", answer: "Real GDP is adjusted for inflation, so it shows actual changes in output rather than just price changes." }
        ]
      }
    ]
  },
  strand3: {
    title: "International Economics",
    overview: "Trade, exchange rates, and the balance of payments in a globalized economy.",
    image: economicsImages.trade,
    units: [
      {
        id: "3.1",
        title: "International Trade",
        objectives: [
          "Explain the benefits of international trade",
          "Understand comparative advantage",
          "Analyze trade barriers and free trade"
        ],
        keyTerms: [
          { term: "International Trade", definition: "The exchange of goods and services between countries." },
          { term: "Absolute Advantage", definition: "When a country can produce a good using fewer resources than another country." },
          { term: "Comparative Advantage", definition: "When a country can produce a good at a lower opportunity cost than another country." },
          { term: "Tariff", definition: "A tax on imported goods designed to protect domestic producers." },
          { term: "Quota", definition: "A limit on the quantity of a good that can be imported." },
          { term: "Free Trade", definition: "Trade between countries without barriers like tariffs or quotas." }
        ],
        studyNotes: `
## Benefits of International Trade

1. **Greater choice** - Access to more goods
2. **Lower prices** - Competition reduces prices
3. **Specialization** - Countries produce what they're best at
4. **Economies of scale** - Larger markets
5. **Technology transfer** - New ideas spread

---

## Comparative Advantage

A country has comparative advantage when it can produce a good at a **lower opportunity cost** than another country.

**Key insight:** Even if one country is better at producing everything, BOTH countries benefit from specialization and trade.

---

## Trade Barriers

### Arguments FOR Protection:
- Protect infant industries
- Protect jobs
- National security
- Prevent dumping

### Arguments FOR Free Trade:
- Lower prices for consumers
- Greater variety
- Efficiency gains
- Economic growth
        `,
        practiceQuestions: [
          { question: "What is comparative advantage?", answer: "When a country can produce a good at a lower opportunity cost than another country." },
          { question: "Give two arguments for free trade.", answer: "Lower prices for consumers, greater variety of goods, efficiency gains, economic growth" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 13 ECONOMICS STUDENT NOTES
// ============================================
export const year13EconomicsStudentNotes = {
  strand1: {
    title: "Advanced Macroeconomic Policy",
    overview: "In-depth analysis of fiscal and monetary policy, and their effects on the economy.",
    units: [
      {
        id: "1.1",
        title: "Fiscal Policy",
        objectives: [
          "Explain fiscal policy instruments",
          "Analyze effects of expansionary and contractionary policy",
          "Evaluate budget deficits and national debt"
        ],
        keyTerms: [
          { term: "Fiscal Policy", definition: "Government use of taxation and spending to influence the economy." },
          { term: "Expansionary Fiscal Policy", definition: "Increasing government spending or cutting taxes to boost economic activity." },
          { term: "Contractionary Fiscal Policy", definition: "Cutting government spending or raising taxes to slow economic activity." },
          { term: "Budget Deficit", definition: "When government spending exceeds tax revenue in a year." },
          { term: "Budget Surplus", definition: "When tax revenue exceeds government spending in a year." },
          { term: "National Debt", definition: "The accumulated total of all past budget deficits - what the government owes." }
        ],
        studyNotes: `
## Fiscal Policy

### Expansionary Policy (Fighting Recession)
- Increase government spending
- Cut taxes
- Effect: AD increases → GDP rises → unemployment falls

### Contractionary Policy (Fighting Inflation)
- Decrease government spending  
- Raise taxes
- Effect: AD decreases → inflation controlled

---

## Budget Position

**Budget Deficit:** G > T (Spending > Tax)
- Government borrows to make up shortfall
- Adds to national debt

**Budget Surplus:** T > G (Tax > Spending)
- Government can pay off debt
- Can save for future

---

## Limitations of Fiscal Policy
1. Time lags (recognition, decision, implementation)
2. Political constraints
3. Crowding out effect
4. Uncertainty of impact
        `,
        practiceQuestions: [
          { question: "What is fiscal policy?", answer: "Government use of taxation and spending to influence the economy." },
          { question: "What fiscal policy would combat a recession?", answer: "Expansionary policy - increase government spending and/or cut taxes to boost aggregate demand." }
        ]
      },
      {
        id: "1.2",
        title: "Monetary Policy",
        objectives: [
          "Explain monetary policy instruments",
          "Analyze effects of interest rate changes",
          "Understand the role of central banks"
        ],
        keyTerms: [
          { term: "Monetary Policy", definition: "Central bank actions to control money supply and interest rates." },
          { term: "Interest Rate", definition: "The cost of borrowing money, expressed as a percentage." },
          { term: "Central Bank", definition: "The institution responsible for monetary policy and financial stability." },
          { term: "Open Market Operations", definition: "Central bank buying/selling government bonds to affect money supply." },
          { term: "Reserve Ratio", definition: "The percentage of deposits banks must keep as reserves." }
        ],
        studyNotes: `
## Monetary Policy Tools

### 1. Interest Rates
- **Lower rates** → More borrowing → More spending → AD rises
- **Higher rates** → Less borrowing → Less spending → AD falls

### 2. Open Market Operations
- **Buy bonds** → Inject money → Money supply rises
- **Sell bonds** → Remove money → Money supply falls

### 3. Reserve Requirements
- **Lower ratio** → Banks can lend more
- **Higher ratio** → Banks lend less

---

## Expansionary vs Contractionary

| Policy | Interest Rates | Effect |
|--------|----------------|--------|
| Expansionary | Lower | Stimulate economy |
| Contractionary | Higher | Cool economy |
        `,
        practiceQuestions: [
          { question: "What is monetary policy?", answer: "Central bank actions to control the money supply and interest rates to influence the economy." },
          { question: "How do lower interest rates affect the economy?", answer: "Lower rates reduce borrowing costs, encouraging spending and investment, which increases aggregate demand and economic activity." }
        ]
      }
    ]
  },
  strand2: {
    title: "Development Economics",
    overview: "Understanding economic development, poverty, and strategies for growth in developing countries.",
    units: [
      {
        id: "2.1",
        title: "Economic Development",
        objectives: [
          "Distinguish between growth and development",
          "Identify indicators of development",
          "Analyze barriers to development"
        ],
        keyTerms: [
          { term: "Economic Growth", definition: "An increase in real GDP - more output being produced." },
          { term: "Economic Development", definition: "Improvement in living standards, health, education, and quality of life." },
          { term: "Human Development Index (HDI)", definition: "A measure of development combining life expectancy, education, and income." },
          { term: "Sustainable Development", definition: "Development that meets present needs without compromising future generations." }
        ],
        studyNotes: `
## Growth vs Development

| Economic Growth | Economic Development |
|-----------------|---------------------|
| Quantitative | Qualitative |
| More GDP | Better lives |
| Narrow measure | Broad measure |
| Can occur without development | Requires growth |

---

## Measuring Development

### Human Development Index (HDI)
Combines:
1. **Health** - Life expectancy
2. **Education** - Years of schooling
3. **Income** - GNI per capita

Scale: 0 to 1 (higher = more developed)

---

## Barriers to Development
1. Poverty trap
2. Lack of infrastructure
3. Poor education and health
4. Corruption
5. Political instability
6. Geographic disadvantages
7. Debt burden
        `,
        practiceQuestions: [
          { question: "Distinguish between economic growth and development.", answer: "Growth is an increase in GDP (quantity); development is an improvement in living standards and quality of life (quality)." },
          { question: "What does HDI measure?", answer: "Human Development Index measures development through health (life expectancy), education (years of schooling), and income (GNI per capita)." }
        ]
      }
    ]
  },
  strand3: {
    title: "Current Economic Issues",
    overview: "Analysis of contemporary economic challenges and policy debates.",
    units: [
      {
        id: "3.1",
        title: "Globalization",
        objectives: [
          "Explain causes and effects of globalization",
          "Evaluate benefits and costs of globalization",
          "Analyze impact on developing countries"
        ],
        keyTerms: [
          { term: "Globalization", definition: "The increasing integration of world economies through trade, investment, and technology." },
          { term: "Multinational Corporation (MNC)", definition: "A company that operates in multiple countries." },
          { term: "Foreign Direct Investment (FDI)", definition: "Investment by a company in business operations in another country." }
        ],
        studyNotes: `
## Globalization

### Causes:
- Reduced trade barriers
- Improved transport
- Advances in ICT
- Deregulation of financial markets
- Growth of MNCs

### Benefits:
- Economic growth
- Job creation
- Technology transfer
- Greater consumer choice
- Competition reduces prices

### Costs:
- Job losses in some sectors
- Environmental damage
- Cultural homogenization
- Widening inequality
- Loss of national sovereignty
        `,
        practiceQuestions: [
          { question: "What is globalization?", answer: "The increasing integration and interdependence of world economies through trade, investment, and technology." },
          { question: "Give two benefits and two costs of globalization.", answer: "Benefits: economic growth, job creation, lower prices. Costs: job losses in some sectors, environmental damage, inequality." }
        ]
      }
    ]
  }
};

// Export default object with all years
export default {
  year11: year11EconomicsStudentNotes,
  year12: year12EconomicsStudentNotes,
  year13: year13EconomicsStudentNotes
};
